import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ArrowLeft, Eye, EyeOff, Crown, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginScreen = ({ onBack }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Ensure value is always a string
    setFormData(prev => ({ ...prev, [name]: String(value || '') }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Use unified login endpoint that detects user role automatically
      // Ensure all data is sent as strings
      const requestData = {
        email: String(formData.email || '').trim(),
        password: String(formData.password || '')
      };
      
      const response = await axios.post(`${API}/auth/unified-login`, requestData);
      
      // Store token and user data (using consistent key names)
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('userRole', response.data.user.role);
      localStorage.setItem('userEmail', response.data.user.email);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      // localStorage.setItem('subscription_active', response.data.user.subscription_active || false);
      localStorage.setItem('subscription_active', response.data.user.subscription_active || true);
      
      console.log('✅ Login successful, stored data:', {
        role: response.data.user.role,
        email: response.data.user.email,
        subscription_active: response.data.user.subscription_active
        // subscription_active: true
      });
      
      // Navigate to appropriate portal based on user role
      const userRole = response.data.user.role;
      if (userRole === 'admin' || userRole === 'super_admin') {
        navigate('/portal/admin');
      } else {
        navigate(`/portal/${userRole}`);
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Log the full error for debugging
      console.error('Full error details:', error.response?.data);
      
      if (error.response?.status === 422) {
        // Validation error
        const validationErrors = error.response.data.detail;
        if (Array.isArray(validationErrors)) {
          // Pydantic validation errors
          const errorMessage = validationErrors.map(err => {
            if (err.loc && err.loc.includes('email')) {
              return 'Please enter a valid email address';
            } else if (err.loc && err.loc.includes('password')) {
              return 'Please enter a valid password';
            } else {
              return err.msg || 'Invalid input';
            }
          }).join(', ');
          setErrors({ general: errorMessage });
        } else if (typeof validationErrors === 'string') {
          setErrors({ general: validationErrors });
        } else {
          setErrors({ general: 'Please check your email and password format' });
        }
      } else if (error.response?.status === 401) {
        const errorDetail = error.response.data.detail;
        localStorage.setItem('signup_email', formData.email);
        if(errorDetail?.requires_verification) {
          navigate(`/verify-email`, { 
                  state: { 
                    requiresPayment: errorDetail?.requires_verification || true,
                    message: errorDetail?.message || 'Please Verify your account!'
                  } 
          });
        }
        setErrors({ general: error.response.data.detail || 'Invalid email or password' });
      } 
    
      else if (error.response?.status === 402) {
        // User exists but hasn't paid - redirect to payment
        const errorDetail = error.response.data.detail;
        localStorage.setItem('signup_email', formData.email);
        navigate('/email-verification', { 
          state: { 
            email: formData.email,
            requiresPayment: errorDetail?.requires_payment || true,
            message: errorDetail?.message || 'Please complete your payment to access the platform'
          } 
        });
        return;
      } else if (error.response?.status === 403) {
        // Handle host approval status
        const errorDetail = error.response.data.detail;
        if (errorDetail?.status === 'pending_approval') {
          setErrors({ general: 'Your host application is pending admin approval. Please wait for approval.' });
        } else if (errorDetail?.status === 'rejected') {
          setErrors({ general: errorDetail?.message || 'Your host application was rejected.' });
        } else {
          setErrors({ general: errorDetail?.message || 'Access denied.' });
        }
      } else {
        setErrors({ general: 'Login failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div 
                className="flex items-center justify-center bg-white shadow-lg overflow-hidden"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png" 
                  alt="Success Club Logo" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    scale: '1.1'
                  }}
                />
              </div>
            </div>
            <CardTitle className="text-2xl text-white flex items-center justify-center gap-2">
              <Users className="w-6 h-6" style={{color: '#D4AF37'}} />
              Success Club Login
            </CardTitle>
            <p className="text-gray-400 mt-2">
              Welcome back to Success Club
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">
                  Email Address
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">
                  Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              {errors.general && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  <p className="text-red-400 text-sm">{errors.general}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>

            <div className="text-center space-y-2">
              <p className="text-gray-400 text-sm">
                Don't have an account?{' '}
                <button
                  onClick={() => navigate('/auth')}
                  className="text-amber-400 hover:text-amber-300 underline"
                >
                  Sign up here
                </button>
              </p>
              <button
                onClick={onBack || (() => navigate('/auth'))}
                className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mx-auto"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to role selection
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="auth-footer">
        <div className="auth-footer-content">
          <div className="auth-footer-links">
            <a href="/features">Features</a>
            <a href="/events">Events</a>
            <a href="/magazine">Magazine</a>
            <a href="/pricing">Pricing</a>
            <a href="/contact">Contact</a>
            <a href="/help-center">Help</a>
            <a href="/privacy-policy">Privacy</a>
          </div>
          <div className="auth-footer-copy">
            © 2025 Success Club. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginScreen;